import { useMemo } from 'react';
import { type DeepPartial, useForm, type UseFormProps, type UseFormReturn } from 'react-hook-form';
import { msg } from '@gonfalon/messages/msg';
import { zodResolver } from '@hookform/resolvers/zod';
import { mergeWith } from 'lodash';
import { z } from 'zod';

import {
  columnsSchema,
  contextMappingsSchema,
  requireActiveInputField,
  timestampColumnSchema,
  warehouseDataSourceFields,
} from '../DataSources/dataSourceFormSchema';

// Aligns with AssignmentDataSourcePost columnMappings field names.
export const assignmentDataSourceObjectSchema = z.object({
  dataSource: warehouseDataSourceFields.extend({
    columnMappings: z.object({
      columns: columnsSchema,
      timestampColumn: timestampColumnSchema,
      experimentIdColumn: z
        .string()
        .min(1, { error: () => msg('metrics.assignmentDataSourceForm.experimentIdColumnRequired') }),
      variantIdColumn: z
        .string()
        .min(1, { error: () => msg('metrics.assignmentDataSourceForm.variantIdColumnRequired') }),
      contexts: contextMappingsSchema,
    }),
  }),
});

export const assignmentDataSourceSchema = assignmentDataSourceObjectSchema.superRefine(requireActiveInputField);

export type AssignmentDataSourceEditorFormInput = z.input<typeof assignmentDataSourceSchema>;
export type AssignmentDataSourceEditorForm = z.output<typeof assignmentDataSourceSchema>;
export type AssignmentDataSourceEditorFormReturn = UseFormReturn<
  AssignmentDataSourceEditorFormInput,
  unknown,
  AssignmentDataSourceEditorForm
>;

export function isAssignmentDataSourceEditorFormValid(formValues: unknown) {
  return assignmentDataSourceSchema.safeParse(formValues).success;
}

const emptyDefaultProps = {};

interface AssignmentDataSourceFormProps {
  defaultValues?: DeepPartial<AssignmentDataSourceEditorFormInput>;
  mode?: UseFormProps['mode'];
}

export const useAssignmentDataSourceForm = (props: AssignmentDataSourceFormProps = emptyDefaultProps) => {
  const mergedDefaultValues = useMemo(() => {
    const defaultValues = {
      dataSource: {
        archived: false,
        columnMappings: {
          columns: [],
          contexts: [['', '']],
        },
        tags: [],
        inputMethod: 'query' as 'query' | 'table',
        sqlQuery: '',
        dbTable: '',
      },
    };

    const merged = mergeWith(defaultValues, props.defaultValues, (objValue, srcValue) => {
      if (Array.isArray(objValue)) {
        return srcValue;
      }
    });

    if (props.defaultValues?.dataSource?.inputMethod === undefined) {
      merged.dataSource.inputMethod = merged.dataSource.dbTable?.trim() ? 'table' : 'query';
    }

    return merged;
  }, [props.defaultValues]);

  return useForm<AssignmentDataSourceEditorFormInput, unknown, AssignmentDataSourceEditorForm>({
    defaultValues: mergedDefaultValues,
    resolver: zodResolver(assignmentDataSourceSchema),
    mode: props.mode ?? 'onSubmit',
    reValidateMode: 'onChange',
  });
};
